import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
    DragDropContext,
    Droppable,
    Draggable,
} from 'react-beautiful-dnd';

import {
    useWindowScroll,
} from 'bdvp-common/src/hooks';

import {
    useMediaConfigReel,
    useDictionary,
    useLang,
} from 'bdvp-common/src/hooks/config';

import Card from './PopoverCard';
import ReelCard from './ReelCard';
import ScrollContainerWithArrows from './PopoverScroll';

import { Tracking } from 'bdvp-tracking';

const dropZonePaddingLeft = 13;

const DropZone = styled.div`
    display: flex;
    align-items: start;
    min-width: 590px;
    min-height: 188px;
    padding-left: ${dropZonePaddingLeft}px;
    padding-right: 5px;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    user-select: none;
`;

const Container = styled.div`
    flex-grow: 1;
    display: inline-flex;
`;

const LoadingCard = styled(ReelCard).attrs({
    isLoading: true,
    orientation: 'portrait',
})`
    width: 220px;
    display: inline-block;
    padding-left: ${dropZonePaddingLeft}px;
    padding-right: 5px;
`;

const getRenderItem = (items, props) => (provided, snapshot, rubric) => {

    return (
        <Card
            mediaPlayback={items[rubric.source.index]}
            draggableProps={provided.draggableProps}
            dragHandleProps={provided.dragHandleProps}
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            {...props}
        />
    );

};

const onDragStart = () => {

    Tracking.trackDragReelClick();

};

const List = (props) => {

    const {
        reel,
        clipsLoading,
        clipsData,
        openClipTrimming,
        removeClip,
        reorder,
    } = props;

    const config = useMediaConfigReel();
    const lang = useLang();
    const dictionary = useDictionary();

    const {
        enableWindowScroll,
        disableWindowScroll,
    } = useWindowScroll();

    let dropZoneRef = React.useRef(null);

    const onBeforeDragStart = () => {

        disableWindowScroll();

    };

    const onDragEnd = (result) => {

        enableWindowScroll();

        if (!result.destination) {

            return;

        }

        if (result.destination.index === result.source.index) {

            return;

        }

        reorder(
            result.source.index,
            result.destination.index
        );

    };

    const renderItem = getRenderItem(clipsData, {
        config,
        lang,
        openClipTrimming,
        removeClip,
        dictionary,
        reel,
    });

    const setDropZoneRef = (ref, provided) => {

        provided.innerRef(ref);

        dropZoneRef.current = ref;

    };

    return (
        <DragDropContext
            onDragEnd={onDragEnd}
            onBeforeDragStart={onBeforeDragStart}
            onDragStart={onDragStart}
        >
            <Droppable
                droppableId={'reelClipsList'}
                direction={'horizontal'}
                renderClone={renderItem}
            >
                {provided => (
                    <Wrapper {...provided.droppableProps} >
                        <ScrollContainerWithArrows dropZoneRef={dropZoneRef} dropZonePaddingLeft={dropZonePaddingLeft}>
                            <Container>
                                <DropZone ref={(ref) => setDropZoneRef(ref, provided)}>
                                    {clipsLoading
                                        ? reel.reelClips.map((item) => (
                                            <LoadingCard key={item.clipSlug} />
                                        ))
                                        : clipsData.map((item, index) => (
                                            <Draggable
                                                key={item.id}
                                                draggableId={item.id}
                                                index={index}
                                            >
                                                {renderItem}
                                            </Draggable>
                                        ))
                                    }
                                    {provided.placeholder}
                                </DropZone>
                            </Container>
                        </ScrollContainerWithArrows>
                    </Wrapper>
                )}
            </Droppable>
        </DragDropContext>
    );

};

List.propTypes = {
    openClipTrimming: PropTypes.func.isRequired,
    removeClip: PropTypes.func.isRequired,
    reorder: PropTypes.func.isRequired,
    clipsLoading: PropTypes.bool.isRequired,
    reel: PropTypes.object.isRequired,
    clipsData: PropTypes.array.isRequired,
};

export default List;
